import React, { useState, useContext, useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import { FaUserCircle, FaCamera, FaSave, FaEdit, FaMapMarkerAlt, FaPhone, FaEnvelope, FaTree, FaHashtag } from 'react-icons/fa';
import Context from '../context';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import service from '../common/HttpService';
import { useDispatch, useSelector } from 'react-redux';
import { viewUserProfile } from '../store/userSlice';
import useTranslate from '../hooks/useTranslate';
import { fetchCropsList } from '../helpers/cropHelper';

const UserProfileDrawer = ({ onClose }) => {
    const { fetchUserDetails } = useContext(Context);
    const user = useSelector(state => state?.user?.user);
    const dispatch = useDispatch();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(user?.profilePic || null);
    const [crops, setCrops] = useState([]);
    const [loadingCrops, setLoadingCrops] = useState(false);

    const t = useTranslate({
        myProfile: "My Profile",
        personalDetails: "Personal Details",
        editProfile: "Edit Profile",
        updateDetails: "Update Your Details",
        firstNameLabel: "First Name",
        lastNameLabel: "Last Name",
        emailLabel: "Email Address (Optional)",
        mobileLabel: "Mobile Number",
        villageLabel: "Village",
        cropLabel: "Primary Crop",
        pincodeLabel: "Pincode",
        stateLabel: "State",
        cityLabel: "City",
        farmSizeLabel: "Farm Size (Acres)",
        saveChanges: "Save Changes",
        saving: "Saving...",
        cancel: "Cancel",
        updateSuccess: "Profile updated successfully",
        updateError: "Failed to update profile",
        imageUploadSuccess: "Image uploaded successfully",
        selectCrop: "Select Crop"
    });

    const [formData, setFormData] = useState({
        first_name: user?.first_name || user?.name?.split(' ')[0] || '',
        last_name: user?.last_name || user?.name?.split(' ')[1] || '',
        email: user?.email || '',
        phone: user?.phone || '',
        village: user?.village || '',
        crop_id: user?.crop_id || '',
        pincode: user?.pincode || '',
        state: user?.state || '',
        city: user?.city || '',
        acrs: user?.acrs || '',
        profilePic: user?.profilePic || ''
    });

    useEffect(() => {
        let isMounted = true;
        const loadCrops = async () => {
            setLoadingCrops(true);
            const list = await fetchCropsList();
            if (isMounted && list && list.length > 0) {
                setCrops(list);
            }
            if (isMounted) setLoadingCrops(false);
        };
        loadCrops();
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (user) {
            setFormData({
                first_name: user?.first_name || user?.name?.split(' ')[0] || '',
                last_name: user?.last_name || user?.name?.split(' ')[1] || '',
                email: user?.email || '',
                phone: user?.phone || '',
                village: user?.village || '',
                crop_id: user?.crop_id || '',
                pincode: user?.pincode || '',
                state: user?.state || '',
                city: user?.city || '',
                acrs: user?.acrs || '',
                profilePic: user?.profilePic || ''
            });
            setImagePreview(user?.profilePic || null);
        }
    }, [user]);

    useEffect(() => {
        window.__activeModals = (window.__activeModals || 0) + 1;
        document.body.style.overflow = 'hidden';
        return () => {
            window.__activeModals = (window.__activeModals || 0) - 1;
            if (window.__activeModals <= 0) {
                document.body.style.overflow = 'unset';
                window.__activeModals = 0;
            }
        };
    }, []);

    const matchedCrop = crops.find(c =>
        String(c.id) === String(formData.crop_id) ||
        c.name.trim().toLowerCase() === String(formData.crop_id).trim().toLowerCase() ||
        (String(formData.crop_id).toLowerCase() === 'rice' && c.name.toLowerCase().includes('paddy')) ||
        (String(formData.crop_id).toLowerCase() === 'wheat' && c.name.toLowerCase().includes('wheat'))
    );
    const currentCropId = matchedCrop ? matchedCrop.id : (formData.crop_id || '');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCropChange = async (e) => {
        const selectedCropId = e.target.value;
        if (!selectedCropId) return;

        setFormData(prev => ({ ...prev, crop_id: selectedCropId }));

        // If in view mode, update crop immediately via API
        if (!isEditing) {
            setLoading(true);
            try {
                const payload = {
                    ...formData,
                    crop_id: selectedCropId,
                    name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || user?.name || ''
                };
                const response = await service.post(SummaryApi.updateProfile.url, payload);
                if (response.success === "true" || response.success === true) {
                    toast.success(t.updateSuccess);
                    await dispatch(viewUserProfile());
                    if (typeof fetchUserDetails === 'function') fetchUserDetails();
                } else {
                    toast.error(response.message || t.updateError);
                }
            } catch (err) {
                toast.error(t.updateError);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setFormData(prev => ({ ...prev, profilePic: reader.result }));
            };
            reader.readAsDataURL(file);
            toast.success(t.imageUploadSuccess);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const finalCropId = currentCropId || formData.crop_id;
            const payload = {
                ...formData,
                crop_id: finalCropId,
                name: `${formData.first_name} ${formData.last_name}`.trim()
            };
            const response = await service.post(SummaryApi.updateProfile.url, payload);
            if (response.success === "true" || response.success === true) {
                toast.success(t.updateSuccess);
                await dispatch(viewUserProfile());
                fetchUserDetails();
                setIsEditing(false);
            } else {
                toast.error(response.message || t.updateError);
            }
        } catch (error) {
            toast.error(t.updateError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='fixed inset-0 z-[100] flex justify-end'>
            <div className='absolute inset-0 bg-black/40 backdrop-blur-sm animate-fadeIn' onClick={onClose}></div>
            <div className='relative w-full max-w-[450px] bg-[#f8fcf9] h-full shadow-2xl flex flex-col animate-slideInRight overflow-hidden'>
                {/* Header Section */}
                <div className='relative bg-[#143d2c] pt-12 pb-20 px-8 overflow-hidden shrink-0'>
                    <div className='absolute top-0 right-0 w-64 h-64 bg-[#00b259]/10 rounded-full -mr-32 -mt-32 blur-3xl'></div>
                    <div className='absolute bottom-0 left-0 w-48 h-48 bg-[#f39200]/5 rounded-full -ml-24 -mb-24 blur-2xl'></div>

                    <button onClick={onClose} className='absolute top-6 right-6 bg-white/10 hover:bg-white/20 p-2.5 rounded-full text-white transition-all z-20'>
                        <MdClose size={24} />
                    </button>

                    <div className='relative z-10 flex items-center gap-6'>
                        <div className='relative group'>
                            <div className='w-24 h-24 rounded-3xl overflow-hidden border-4 border-white/20 shadow-2xl bg-white/10 backdrop-blur-md'>
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Profile" className='w-full h-full object-cover' />
                                ) : (
                                    <div className='w-full h-full flex items-center justify-center text-white/50'>
                                        <FaUserCircle size={60} />
                                    </div>
                                )}
                            </div>
                            {isEditing && (
                                <label className='absolute -bottom-2 -right-2 bg-[#f39200] p-2.5 rounded-xl text-white cursor-pointer shadow-lg hover:scale-110 transition-all border-2 border-[#143d2c]'>
                                    <FaCamera size={14} />
                                    <input type="file" className='hidden' onChange={handleImageChange} accept="image/*" />
                                </label>
                            )}
                        </div>
                        <div>
                            <h2 className='text-3xl font-black text-white leading-none uppercase tracking-tighter'>
                                {isEditing ? t.editProfile : (user?.name || t.myProfile)}
                            </h2>
                            <p className='text-[#00b259] font-bold text-xs uppercase tracking-[3px] mt-2 opacity-90'>
                                {isEditing ? t.updateDetails : t.personalDetails}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form Section */}
                <div className='flex-1 overflow-y-auto px-8 py-8 -mt-10 relative z-10 custom-scrollbar'>
                    <div className='bg-white rounded-[32px] shadow-xl shadow-green-900/5 p-8 border border-gray-50'>
                        <form onSubmit={handleSubmit} className='space-y-6'>
                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-1.5'>
                                    <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t.firstNameLabel}</label>
                                    <div className='relative'>
                                        <input
                                            type="text"
                                            name="first_name"
                                            value={formData.first_name}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            className='w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259] focus:bg-white rounded-2xl px-4 py-3.5 text-sm font-bold text-slate-700 outline-none transition-all disabled:opacity-60'
                                        />
                                    </div>
                                </div>
                                <div className='space-y-1.5'>
                                    <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t.lastNameLabel}</label>
                                    <div className='relative'>
                                        <input
                                            type="text"
                                            name="last_name"
                                            value={formData.last_name}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            className='w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259] focus:bg-white rounded-2xl px-4 py-3.5 text-sm font-bold text-slate-700 outline-none transition-all disabled:opacity-60'
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className='space-y-1.5'>
                                <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t.emailLabel}</label>
                                <div className='relative flex items-center'>
                                    <FaEnvelope className='absolute left-4 text-gray-300' size={14} />
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        placeholder="Optional"
                                        className='w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259] focus:bg-white rounded-2xl pl-12 pr-4 py-3.5 text-sm font-bold text-slate-700 outline-none transition-all disabled:opacity-60'
                                    />
                                </div>
                            </div>

                            <div className='space-y-1.5'>
                                <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t.mobileLabel}</label>
                                <div className='relative flex items-center'>
                                    <FaPhone className='absolute left-4 text-gray-300' size={14} />
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        disabled={true} // Mobile typically not editable
                                        className='w-full bg-gray-100/50 border-2 border-transparent rounded-2xl pl-12 pr-4 py-3.5 text-sm font-bold text-gray-400 outline-none cursor-not-allowed'
                                    />
                                </div>
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-1.5'>
                                    <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t.villageLabel}</label>
                                    <div className='relative flex items-center'>
                                        <FaMapMarkerAlt className='absolute left-4 text-gray-300' size={14} />
                                        <input
                                            type="text"
                                            name="village"
                                            value={formData.village}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            className='w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259] focus:bg-white rounded-2xl pl-11 pr-4 py-3.5 text-sm font-bold text-slate-700 outline-none transition-all disabled:opacity-60'
                                        />
                                    </div>
                                </div>
                                <div className='space-y-1.5'>
                                    <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t.cropLabel}</label>
                                    <div className='relative flex items-center'>
                                        <FaTree className='absolute left-4 text-gray-300 pointer-events-none' size={14} />
                                        <select
                                            name="crop_id"
                                            value={currentCropId}
                                            onChange={handleCropChange}
                                            disabled={loading}
                                            className='w-full bg-gray-50 border-2 border-transparent hover:border-[#00b259]/50 focus:border-[#00b259] focus:bg-white rounded-2xl pl-11 pr-8 py-3.5 text-sm font-bold text-slate-700 outline-none transition-all appearance-none cursor-pointer disabled:opacity-60'
                                        >
                                            <option value="" disabled>{loadingCrops ? "Loading crops..." : t.selectCrop}</option>
                                            {crops.map((crop) => (
                                                <option key={crop.id} value={crop.id}>
                                                    {crop.name}
                                                </option>
                                            ))}
                                            {formData.crop_id && !crops.some(c => String(c.id) === String(currentCropId)) && (
                                                <option value={formData.crop_id}>
                                                    {formData.crop_id}
                                                </option>
                                            )}
                                        </select>
                                        <div className="absolute right-4 pointer-events-none text-gray-400">
                                            {loading ? (
                                                <div className="w-3.5 h-3.5 border-2 border-[#00b259]/30 border-t-[#00b259] rounded-full animate-spin"></div>
                                            ) : (
                                                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
                                                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-1.5'>
                                    <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t.pincodeLabel}</label>
                                    <div className='relative flex items-center'>
                                        <FaHashtag className='absolute left-4 text-gray-300' size={12} />
                                        <input
                                            type="text"
                                            name="pincode"
                                            value={formData.pincode}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            className='w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259] focus:bg-white rounded-2xl pl-11 pr-4 py-3.5 text-sm font-bold text-slate-700 outline-none transition-all disabled:opacity-60'
                                        />
                                    </div>
                                </div>
                                <div className='space-y-1.5'>
                                    <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t.farmSizeLabel}</label>
                                    <input
                                        type="number"
                                        name="acrs"
                                        value={formData.acrs}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className='w-full bg-gray-50 border-2 border-transparent focus:border-[#00b259] focus:bg-white rounded-2xl px-4 py-3.5 text-sm font-bold text-slate-700 outline-none transition-all disabled:opacity-60'
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className='p-8 bg-white border-t border-gray-100 shrink-0'>
                    {isEditing ? (
                        <div className='flex gap-4 animate-fadeIn'>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(false);
                                    setFormData({
                                        first_name: user?.first_name || user?.name?.split(' ')[0] || '',
                                        last_name: user?.last_name || user?.name?.split(' ')[1] || '',
                                        email: user?.email || '',
                                        phone: user?.phone || '',
                                        village: user?.village || '',
                                        crop_id: user?.crop_id || '',
                                        pincode: user?.pincode || '',
                                        state: user?.state || '',
                                        city: user?.city || '',
                                        acrs: user?.acrs || '',
                                        profilePic: user?.profilePic || ''
                                    });
                                    setImagePreview(user?.profilePic || null);
                                }}
                                className='flex-1 bg-gray-50 hover:bg-gray-100 text-gray-500 py-4 rounded-2xl font-black text-[11px] uppercase tracking-[2px] transition-all'
                            >
                                {t.cancel}
                            </button>
                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={loading}
                                className='flex-[2] bg-gradient-to-r from-[#00b259] to-[#008a44] text-white py-4 rounded-2xl font-black text-[11px] uppercase tracking-[2px] shadow-lg shadow-green-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 border-b-4 border-[#006e36] disabled:opacity-50'
                            >
                                {loading ? (
                                    <div className='w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin'></div>
                                ) : <FaSave size={14} />}
                                {loading ? t.saving : t.saveChanges}
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setIsEditing(true)}
                            className='w-full bg-[#143d2c] hover:bg-[#1a4a36] text-white py-5 rounded-[24px] font-black text-[11px] uppercase tracking-[3px] shadow-xl shadow-green-900/10 active:scale-[0.98] transition-all flex items-center justify-center gap-3 border-b-4 border-[#0d2a1f]'
                        >
                            <FaEdit size={16} /> {t.editProfile}
                        </button>
                    )}
                </div>
            </div>

            <style jsx="true">{`
 .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
 .animate-slideInRight { animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
 @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
 @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
 .custom-scrollbar::-webkit-scrollbar { width: 4px; }
 .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,178,89,0.1); border-radius: 10px; }
 `}</style>
        </div>
    );
};

export default UserProfileDrawer;
